/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useId, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Check, type LucideIcon } from 'lucide-react';
import { Button, type ButtonVariant } from './Button';
import { Icon } from './Icon';

export interface MenuItem {
  id: string;
  label: ReactNode;
  icon?: LucideIcon;
  /** Shown as a radio choice with a check when set. */
  checked?: boolean;
  onSelect: () => void;
}

export interface MenuButtonProps {
  /** The button's accessible name. */
  label: string;
  icon?: LucideIcon;
  /** Visible text on the button (an icon-only button when absent). */
  text?: ReactNode;
  items: MenuItem[];
  /** Which way the menu opens: `up` from the foot of the rail, `down` from a header. */
  placement?: 'up' | 'down';
  align?: 'start' | 'end';
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  /** A heading inside the menu. */
  heading?: string;
}

/** A button that opens a short menu (the theme picker, a row's "…" actions). Arrow keys move; Esc, Tab out and a click outside close. */
export function MenuButton({ label, icon, text, items, placement = 'down', align = 'end', variant = 'ghost', size = 'sm', heading }: MenuButtonProps) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const radio = items.some((item) => item.checked !== undefined);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    const checked = wrap.current?.querySelector<HTMLButtonElement>('[role^="menuitem"][aria-checked="true"]');
    (checked ?? wrap.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]'))?.focus();
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) wrap.current?.querySelector<HTMLButtonElement>('button[aria-haspopup]')?.focus();
  };

  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const entries = Array.from(wrap.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]') ?? []);
    const at = entries.indexOf(document.activeElement as HTMLButtonElement);
    const next = entries[(at + (event.key === 'ArrowDown' ? 1 : -1) + entries.length) % entries.length];
    next?.focus();
  };

  // Tab (or Shift Tab) out of the menu closes it: focus has landed outside the wrap, in the page or nowhere.
  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!wrap.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
  };

  return (
    <div className="ui-menu-wrap" ref={wrap} onKeyDown={open ? onKey : undefined} onBlur={open ? onBlur : undefined}>
      <Button
        variant={variant}
        size={size}
        icon={icon}
        aria-label={text ? undefined : label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        {text}
      </Button>
      {open ? (
        <div id={menuId} role="menu" aria-label={label} className={`ui-menu is-${placement} is-${align}`}>
          {heading ? <p className="ui-menu-heading" aria-hidden="true">{heading}</p> : null}
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              role={radio ? 'menuitemradio' : 'menuitem'}
              aria-checked={radio ? Boolean(item.checked) : undefined}
              className="ui-menu-item"
              onClick={() => {
                item.onSelect();
                close(true);
              }}
            >
              {item.icon ? <Icon icon={item.icon} size="md" /> : null}
              <span className="ui-menu-label">{item.label}</span>
              {item.checked ? <Icon icon={Check} size="sm" className="ui-menu-check" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
